// ui/src/components/padrekko/qc/ValidationState.jsx
//
// What a finding's validation says, for whoever is looking.
//
// ── RENDERS FOR EVERY VIEWER. THERE IS NO WRITEABILITY PROP. ─────────────────
//
// That absence is deliberate and structural: this component cannot be gated by
// role, because it is handed nothing to gate on.
//
// An operator who cannot validate anything seeing
//
//     Validated · S. Martinez · University Lands · 14 Apr 2026
//
// on a finding IS the evidence. It is the entire reason the capability exists.
// Gate the state along with the button and the gap closes in the database while
// staying open in the artifact people outside actually read — and it would look
// like a finished feature from the inside, which is how that mistake survives.
//
// Only the ACTION is gated. Never the state.
//
// Everything here is server-derived (validationState.js). Nothing in this file
// decides whether a validation is current.

import { BadgeCheck, BadgeAlert, AlertTriangle } from "lucide-react";
// No demoOrgs import. Names arrive resolved from the server, snapshotted into
// the event at write time — see lib/padrekko-qc/actorNames.js. Resolving them
// here meant a hardcoded three-org list whose miss path was orgId.slice(0, 8),
// so a fourth tenant rendered as `a6c3e2b2` in the line whose whole job is
// naming who affirmed the finding.
const UNKNOWN_USER = "Unknown user";
const UNKNOWN_ORG = "Unknown org";

// Date, not time-of-day. A validation is a historical fact whose day matters
// and whose minute does not — unlike the card's other timestamps, which are
// about what happened during this sitting.
export function formatStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

// Mirrors MICRO_PILL in QcFindingCard so the validation pill sits flush with
// the disputed and kicked-back pills it shares a row with.
/**
 * Stage keys → the words a reader uses.
 *
 * Role-generic, never an organisation. The same rule stageLabels.js holds on
 * the server, and for the same reason: "Validated at UL review" would ship one
 * customer's initials to every tenant. Falls through to the raw key rather than
 * inventing a label for a stage this file has not been taught.
 */
const STAGE_WORD = Object.freeze({
  dh_review: "DH review",
  customer_review: "customer review",
  operator_review: "operator review",
  complete: "close",
});

const PILL = "text-[9px] rounded border flex items-center justify-center " +
             "p-[3px] whitespace-nowrap shrink-0";

const Dot = () => <span className="text-slate-600">·</span>;

/**
 * The COLLAPSED-row indicator. Presence and currency only.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * ValidationState below renders only in the card's expanded body, and cards are
 * collapsed by default. So the full byline satisfied "renders for every viewer"
 * with respect to ROLE while being gated behind a click — which is still
 * gating it. A stale validation, the state the whole hash design exists to
 * produce, was invisible until someone happened to open that particular card.
 *
 * The requirement it now meets: a viewer can scan a pad and tell which findings
 * carry a current affirmation, which carry a stale one, and which carry none,
 * without clicking anything.
 *
 * ── WHY A HEADER PILL ───────────────────────────────────────────────────────
 *
 * Not invented: DisputedPill and KickedBackPill already do exactly this — a
 * compact state indicator in the always-visible header, with the detail in the
 * expanded body. Validation is the same shape of fact, so it takes the same
 * shape of affordance rather than a second vocabulary for the reader to learn.
 *
 * ── NONE RENDERS NOTHING ────────────────────────────────────────────────────
 *
 * Across 31 findings, a pill on every unvalidated card would be noise that
 * buries the six that carry something. Absence stays silent; that is what makes
 * presence legible.
 */
export function ValidationPill({ validation }) {
  const state = validation?.state ?? "none";
  if (state === "none") return null;

  const v = validation.validation;
  const who = v?.by_user_name || UNKNOWN_USER;
  const org = v?.by_org_name || UNKNOWN_ORG;
  const when = formatStamp(v?.at);

  if (state === "current") {
    const label = `Affirmed by ${who}${org ? ` · ${org}` : ""}${when ? ` · ${when}` : ""}`;
    return (
      <span
        className={`${PILL} bg-emerald-500/15 border-emerald-500/50 text-emerald-200`}
        title={label + (v?.note ? `\n“${v.note}”` : "")}
        aria-label={label}
      >
        <BadgeCheck size={9} />
      </span>
    );
  }

  if (state === "other_stage") {
    // VALIDATED, BUT NOT HERE.
    //
    // Slate, not emerald and not amber. Green would claim this stage's
    // reviewer has signed off when they have not — which is exactly the bug
    // this state exists to fix — and amber would claim something is wrong,
    // when nothing is. It is a fact from an earlier stage that still stands,
    // and the neutral tone is the honest one: visible, legible, and making no
    // claim about the current stage.
    const label =
      `Affirmed at ${v?.case_stage ?? "an earlier stage"} by ${who}` +
      `${org ? ` · ${org}` : ""}${when ? ` · ${when}` : ""}.` +
      ` Not yet affirmed at this stage.`;
    return (
      <span
        className={`${PILL} bg-slate-600/25 border-slate-500/50 text-slate-300`}
        title={label + (v?.note ? `\n“${v.note}”` : "")}
        aria-label={label}
      >
        <BadgeCheck size={9} />
      </span>
    );
  }

  // BadgeAlert, NOT AlertTriangle.
  //
  // DisputedPill is an amber AlertTriangle with byte-identical tone classes —
  // this pill was originally a copy of it. With words they read differently;
  // icon-only they were the same pixel, on a card that can carry both at once.
  //
  // The badge silhouette is shared with BadgeCheck above, so the shape says
  // "this is about validation" and the mark inside says which. The triangle now
  // means disputed and nothing else.
  const label = `Affirmation superseded — the analysis changed after it.`
    + ` Previously affirmed by ${who}${org ? ` · ${org}` : ""}${when ? ` · ${when}` : ""}`
    + `${validation.superseded_at ? ` · superseded ${formatStamp(validation.superseded_at)}` : ""}`;
  return (
    <span
      className={`${PILL} bg-amber-500/15 border-amber-500/50 text-amber-200`}
      title={label}
      aria-label={label}
    >
      <BadgeAlert size={9} />
    </span>
  );
}

export default function ValidationState({ validation }) {
  const state = validation?.state ?? "none";

  // Nothing to say, so say nothing. Deliberately not "Not validated" — an
  // absence of evidence should not be rendered as a finding about the finding.
  if (state === "none") return null;

  const v = validation.validation;
  const who = v?.by_user_name || UNKNOWN_USER;
  const org = v?.by_org_name || UNKNOWN_ORG;
  const when = formatStamp(v?.at);

  if (state === "other_stage") {
    return (
      <div className="flex items-center gap-1.5 flex-wrap text-[9px] text-slate-400">
        <BadgeCheck size={11} className="shrink-0" />
        {/* Says WHICH stage. The bare word alone here would be read as this
            stage's judgment, which is the misreading the scoping fixed. The word
            itself is not written in this comment: the display-word guard is a
            text scan and cannot tell a JSX comment from rendered copy. */}
        <span className="font-medium">
          Affirmed at {STAGE_WORD[v?.case_stage] ?? v?.case_stage ?? "an earlier stage"}
        </span>
        <Dot /><span>{who}</span>
        {org && (<><Dot /><span>{org}</span></>)}
        {when && (<><Dot /><span>{when}</span></>)}
        {v?.note && (
          <span className="text-fg-secondary italic truncate max-w-[14rem]" title={v.note}>
            “{v.note}”
          </span>
        )}
      </div>
    );
  }

  if (state === "current") {
    return (
      <div className="flex items-center gap-1.5 flex-wrap text-[9px] text-emerald-300/90">
        <BadgeCheck size={11} className="shrink-0" />
        <span className="font-medium">Affirmed</span>
        <Dot /><span>{who}</span>
        {org && (<><Dot /><span>{org}</span></>)}
        {when && (<><Dot /><span>{when}</span></>)}
        {v?.note && (
          <span className="text-fg-secondary italic truncate max-w-[14rem]" title={v.note}>
            “{v.note}”
          </span>
        )}
      </div>
    );
  }

  // ── STALE ────────────────────────────────────────────────────────────────
  //
  // The state most likely to be waved off as an edge case, and the one that
  // proves the content-hash binding does its job: an old judgment must not
  // silently stand against new evidence.
  //
  // The prior validation stays fully legible — actor, org, stage, time —
  // because it really did happen and the record says so. It is marked
  // superseded, not erased. Struck through rather than removed for the same
  // reason.
  const supersededWhen = formatStamp(validation.superseded_at);
  return (
    <div className="flex flex-col gap-0.5 text-[9px]">
      <div className="flex items-center gap-1.5 flex-wrap text-amber-300/90">
        <AlertTriangle size={11} className="shrink-0" />
        <span className="font-medium">Affirmation superseded</span>
        {supersededWhen && (<><Dot /><span>analysis changed {supersededWhen}</span></>)}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-fg-secondary line-through decoration-slate-600">
        <span>Affirmed</span>
        <Dot /><span>{who}</span>
        {org && (<><Dot /><span>{org}</span></>)}
        {v?.case_stage && (<><Dot /><span>{v.case_stage}</span></>)}
        {when && (<><Dot /><span>{when}</span></>)}
      </div>
    </div>
  );
}
